import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from './Sidebar';
import PWAInstallPrompt from './PWAInstallPrompt';
import MobileBottomNav from '../mobile/MobileBottomNav';
import FloatingActionButton from '../mobile/FloatingActionButton';
import OfflineIndicator from '../mobile/OfflineIndicator';
import { useIsMobile } from '../../hooks/useMediaQuery';
import Avatar from './Avatar';
import { NotificationBell } from '../notifications';
import { Menu, Moon, Sun, User, Bell, Settings, CreditCard, Users, LogOut } from 'lucide-react';

interface SidebarLayoutProps {
  children?: React.ReactNode;
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children }) => {
  const { state, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isCollapsed, toggleMobile } = useSidebar();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Helper function to check user role (handles both string and array formats)
  const hasRole = (role: 'Customer' | 'ShopOwner' | 'Admin' | 'Barber'): boolean => {
    if (!state.user?.role) return false;
    const userRole = state.user.role;
    if (Array.isArray(userRole)) {
      return userRole.includes(role);
    }
    return userRole === role;
  };

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const getProfilePath = () => {
    // Use hasRole helper to handle both string and array role formats
    if (hasRole('Barber')) return '/barber/profile';
    if (hasRole('Customer')) return '/profile';
    if (hasRole('ShopOwner')) return '/shop-owner/subscription';
    if (hasRole('Admin')) return '/admin/dashboard';
    return '/dashboard';
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-black transition-colors">
      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* Sidebar */}
      <Sidebar />

      {/* Main content area */}
      <div
        className={`
          transition-all duration-300 ease-in-out
          ${state.isAuthenticated ? `lg:ml-${isCollapsed ? '16' : '64'}` : ''}
        `}
        style={{
          marginLeft: state.isAuthenticated && !isMobile ? (isCollapsed ? '4rem' : '16rem') : 0,
        }}
      >
        {/* Top header bar for mobile and desktop */}
        <header className="sticky top-0 z-20 bg-white dark:bg-gray-800 shadow-sm border-b border-neutral-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-16 px-4">
            {/* Mobile menu button and logo */}
            <div className="flex items-center gap-3">
              {state.isAuthenticated && (
                <button
                  onClick={toggleMobile}
                  className="lg:hidden p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Open sidebar"
                >
                  <Menu size={24} />
                </button>
              )}

              {/* Mobile logo */}
              <Link to="/" className="lg:hidden flex items-center gap-2">
                <img
                  src="/BarbrIQ-logo.jpeg"
                  alt="BarbrIQ Logo"
                  className="h-8 w-auto object-contain"
                />
                <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
                  BarbrIQ
                </span>
              </Link>
            </div>

            {/* Right side controls */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* PWA Install */}
              <PWAInstallPrompt className="hidden sm:flex text-xs px-2 py-1" />

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-neutral-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
              </button>

              {/* Notification Bell */}
              {state.isAuthenticated && <NotificationBell />}

              {/* User Avatar with Dropdown Menu */}
              {state.isAuthenticated && (
                <div ref={userMenuRef} className="relative hidden sm:block">
                  <button
                    onClick={toggleUserMenu}
                    className="p-1 hover:ring-2 hover:ring-primary-500 transition-all rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label="User menu"
                  >
                    <Avatar
                      firstName={state.user?.firstName}
                      lastName={state.user?.lastName}
                      profilePictureUrl={state.user?.profilePictureUrl}
                      size="sm"
                    />
                  </button>

                  {/* User Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {state.user?.firstName} {state.user?.lastName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {state.user?.email}
                        </p>
                      </div>

                      <Link
                        to={getProfilePath()}
                        onClick={() => setIsUserMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-3" />
                          View/Edit Profile
                        </div>
                      </Link>

                      <Link
                        to="/notifications"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center">
                          <Bell className="w-4 h-4 mr-3" />
                          Notifications
                        </div>
                      </Link>

                      {hasRole('Customer') && (
                        <>
                          <Link
                            to="/profile?tab=preferences"
                            onClick={() => setIsUserMenuOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="flex items-center">
                              <Settings className="w-4 h-4 mr-3" />
                              Preferences
                            </div>
                          </Link>

                          <Link
                            to="/memberships/dashboard"
                            onClick={() => setIsUserMenuOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="flex items-center">
                              <Users className="w-4 h-4 mr-3" />
                              Manage Memberships
                            </div>
                          </Link>
                        </>
                      )}

                      {hasRole('ShopOwner') && (
                        <Link
                          to="/shop-owner/subscription"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="flex items-center">
                            <CreditCard className="w-4 h-4 mr-3" />
                            Manage Subscription
                          </div>
                        </Link>
                      )}

                      {hasRole('Barber') && (
                        <Link
                          to="/barber/subscription"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="flex items-center">
                            <CreditCard className="w-4 h-4 mr-3" />
                            Manage Subscription
                          </div>
                        </Link>
                      )}

                      <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center">
                          <LogOut className="w-4 h-4 mr-3" />
                          Logout
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Login/Register for non-authenticated (mobile) */}
              {!state.isAuthenticated && (
                <div className="flex items-center gap-2">
                  <Link
                    to="/login"
                    className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="hidden sm:inline-flex text-sm font-medium px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1" style={{ paddingBottom: isMobile ? '80px' : '0' }}>
          {children || <Outlet />}
        </main>

        {/* Mobile Navigation */}
        <MobileBottomNav />

        {/* Floating Action Button for Mobile */}
        {state.isAuthenticated && <FloatingActionButton />}

        {/* Footer */}
        <footer className="bg-barbershop-800 dark:bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:py-8 sm:px-6 lg:px-8">
            <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
              <p className="text-barbershop-300 dark:text-gray-400 text-sm text-center sm:text-left">
                &copy; 2025 BarbrIQ. All rights reserved.
              </p>
              <div className="flex justify-center space-x-4 sm:space-x-6">
                <Link
                  to="/terms"
                  className="text-barbershop-300 dark:text-gray-400 hover:text-secondary-400 dark:hover:text-secondary-300 text-sm transition-colors"
                >
                  Terms
                </Link>
                <Link
                  to="/privacy"
                  className="text-barbershop-300 dark:text-gray-400 hover:text-secondary-400 dark:hover:text-secondary-300 text-sm transition-colors"
                >
                  Privacy
                </Link>
                <Link
                  to="/support"
                  className="text-barbershop-300 dark:text-gray-400 hover:text-secondary-400 dark:hover:text-secondary-300 text-sm transition-colors"
                >
                  Support
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default SidebarLayout;
